/**
 * Rozwiniecie wiersza: GET /sfd-zamowienia/<id> (naglowek + pozycje; pierwszy raz backend idzie do portalu,
 * potem cache pozycje_json). Pokazuje adres dostawy, forme platnosci, pelne uwagi i tabele pozycji
 * (nazwa, kod SFD, ilosc, cena netto, VAT, netto, brutto) z suma.
 */
import React, { useEffect, useState } from 'react';
import { sfdZamowieniaService } from '../../services/sfdZamowieniaService';
import { formatujKwote, formatujIlosc, formatujDate, komunikatBledu } from './format';

const th = { padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '3px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'top' };
const prawa = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

const Info = ({ etykieta, wartosc }) => (
  <span style={{ whiteSpace: 'nowrap' }}><span style={{ color: 'var(--text-muted)' }}>{etykieta}:</span> <span style={{ fontWeight: 600 }}>{wartosc || '—'}</span></span>
);

/** Props: id (sfd_zamowienia.id), cache (Map id -> szczegoly, opcjonalnie wspolna dla tabeli). */
const PozycjeZamowienia = ({ id, cache }) => {
  const [dane, setDane] = useState(() => (cache && cache.get(id)) || null);
  const [ladowanie, setLadowanie] = useState(!dane);
  const [blad, setBlad] = useState(null);

  useEffect(() => {
    let aktywny = true;
    if (cache && cache.get(id)) {
      setDane(cache.get(id));
      setLadowanie(false);
      return () => { aktywny = false; };
    }
    setLadowanie(true);
    setBlad(null);
    sfdZamowieniaService.szczegoly(id)
      .then((d) => {
        if (!aktywny) return;
        if (cache) cache.set(id, d);
        setDane(d || null);
      })
      .catch((err) => { if (aktywny && !err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać pozycji zamówienia.')); })
      .finally(() => { if (aktywny) setLadowanie(false); });
    return () => { aktywny = false; };
  }, [id, cache]);

  if (ladowanie) return <div data-testid="pozycje-ladowanie" style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Pobieram pozycje z SFD…</div>;
  if (blad) return <div data-testid="pozycje-blad" style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--danger)' }}>{blad}</div>;

  const pozycje = Array.isArray(dane?.pozycje) ? dane.pozycje : [];
  const suma = (klucz) => pozycje.reduce((s, p) => s + (Number(p[klucz]) || 0), 0);
  return (
    <div data-testid="pozycje-zamowienia" style={{ padding: '6px 12px 10px', backgroundColor: 'var(--bg)', fontSize: '11px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: '6px' }}>
        <Info etykieta="Adres dostawy" wartosc={dane?.adres?.tekst} />
        <Info etykieta="Płatność" wartosc={dane?.forma_platnosci} />
        <Info etykieta="Status płatności" wartosc={dane?.status_platnosci?.etykieta} />
        <Info etykieta="Dostawa" wartosc={dane?.dostawa} />
        <Info etykieta="Status dostawy" wartosc={dane?.status_dostawy?.etykieta} />
        <Info etykieta="Realizacja" wartosc={dane?.data_realizacji ? formatujDate(dane.data_realizacji) : null} />
        <Info etykieta="Nr obcy" wartosc={dane?.numer_obcy} />
      </div>
      {dane?.opis && <div data-testid="pozycje-opis" style={{ marginBottom: '6px', whiteSpace: 'pre-wrap' }}><span style={{ color: 'var(--text-muted)' }}>Uwagi:</span> {dane.opis}</div>}
      {pozycje.length === 0 ? (
        <div data-testid="pozycje-brak" style={{ color: 'var(--text-muted)' }}>Portal nie zwrócił pozycji tego zamówienia</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'right' }}>Lp</th>
                <th style={th}>Nazwa</th>
                <th style={th}>Kod SFD</th>
                <th style={th}>Kod</th>
                <th style={{ ...th, textAlign: 'right' }}>Ilość</th>
                <th style={{ ...th, textAlign: 'right' }}>Cena netto</th>
                <th style={{ ...th, textAlign: 'right' }}>VAT</th>
                <th style={{ ...th, textAlign: 'right' }}>Netto</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto</th>
              </tr>
            </thead>
            <tbody>
              {pozycje.map((p, idx) => (
                <tr key={`${p.lp}-${idx}`} data-testid="pozycja">
                  <td style={prawa}>{p.lp}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{p.nazwa}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.kod_sfd || '—'}</td>
                  <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{p.kod || '—'}</td>
                  <td style={{ ...prawa, fontWeight: 600 }}>{formatujIlosc(p.ilosc)} {p.jednostka || ''}</td>
                  <td style={prawa}>{formatujKwote(p.cena_netto)}</td>
                  <td style={prawa}>{p.vat === null || p.vat === undefined ? '—' : `${formatujIlosc(p.vat)}%`}</td>
                  <td style={prawa}>{formatujKwote(p.netto)}</td>
                  <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(p.brutto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr data-testid="pozycje-suma">
                <td style={{ ...td, borderBottom: 'none' }} colSpan={4}><span style={{ fontWeight: 600 }}>Razem</span> · {pozycje.length} poz.</td>
                <td style={{ ...prawa, borderBottom: 'none', fontWeight: 600 }}>{formatujIlosc(suma('ilosc'))}</td>
                <td style={{ ...td, borderBottom: 'none' }} colSpan={2} />
                <td style={{ ...prawa, borderBottom: 'none', fontWeight: 600 }}>{formatujKwote(suma('netto'))}</td>
                <td style={{ ...prawa, borderBottom: 'none', fontWeight: 700, color: 'var(--orange)' }}>{formatujKwote(suma('brutto'))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default PozycjeZamowienia;
